import React, { useState, useMemo } from "react";
import { Users, Star, DollarSign, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/Tabs";

const ROLE_META = {
  Creator: {
    icon: Star,
    col3: "IU Holdings",
    accessor: (x) => x.iuAllocation ?? 0,
    
  },
  Contributor: {
    icon: Users,
    col3: "IU Holdings",
    accessor: (x) => x.iuAllocation ?? 0,
    displayFormat: (item) => `${item.iuAllocation} IUs`,
  },
  Investor: {
    icon: DollarSign,
    col3: "IU Holdings",
    accessor: (x) => x.iuAllocation ?? 0,
    displayFormat: (item) => `${item.iuAllocation} IUs`,
  },
  Staker: {
    icon: Lock,
    col3: "Amount Staked",
    accessor: (x) => x.stakedAmount ?? 0,
    displayFormat: (item) => `${item.stakedAmount} CEL`,
  },
};

const InnovatorsGrid = ({ role, rows }) => {
  const { icon: Icon, col3, accessor, displayFormat } = ROLE_META[role];

  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        No {role.toLowerCase()}s associated with this project yet.
      </div>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Role
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            Innovator
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            {col3}
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 ? "bg-gray-50" : "bg-white"}>
            <td className="px-6 py-4 whitespace-nowrap flex items-center">
              <Icon className="w-5 h-5 mr-2" />
              <span className="text-sm">{role}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              {row.walletAddress ? (
                <Link 
                  to={`/innovators/${row.walletAddress}`}
                  className="text-indigo-600 hover:text-indigo-900 hover:underline transition-colors"
                >
                  {row.walletAddress}
                </Link>
              ) : (
                "Unknown Address"
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
              {displayFormat ? displayFormat(row) : `${accessor(row)} ${role === "Staker" ? "CEL" : "IUs"}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const InnovatorsTable = ({
  creators = [],
  contributors = [],
  investors = [],
  stakers = [],
}) => {
  const [activeTab, setActiveTab] = useState("Creator");

  const data = useMemo(
    () => ({
      Investor: investors.map((x) => ({ ...x, role: "Investor" })),
      Staker: stakers.map((x) => ({ ...x, role: "Staker" })),
      Creator: creators.map((x) => ({ ...x, role: "Creator" })),
      Contributor: contributors.map((x) => ({ ...x, role: "Contributor" })),
    }),
    [investors, stakers]
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow overflow-hidden mb-4">
      <div className="px-4 py-4 border-b border-gray-200">
       
      </div>

      <div className="p-4 bg-white">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v)}>
          <TabsList className="mb-2 flex flex-wrap">
            <TabsTrigger value="Creator">Creators</TabsTrigger>
            <TabsTrigger value="Investor">Investors</TabsTrigger>
            <TabsTrigger value="Staker">Stakers</TabsTrigger>
            <TabsTrigger value="Contributor">Contributors</TabsTrigger>
          </TabsList>

          {["Investor", "Staker", "Creator", "Contributor"].map((role) => (
            <TabsContent key={role} value={role}>
              <InnovatorsGrid role={role} rows={data[role]} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default InnovatorsTable;
